'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import type { SupportKind } from '@/lib/schemas/support.schema';
import { SupportButton } from './SupportButton';
import { SupportPanel } from './SupportPanel';
import type { DraftShot } from './ShotsField';
import { collector } from '@/lib/support/collector';
import type { LiveEventsHandle, SupportEvent } from '@/hooks/useLiveEvents';
import { parseTicketNumber } from '@/lib/schemas/support.schema';
import { LEGAL_PATHS } from '@/lib/legal';

// ── Le support dans toute l'application : le bouton, le panneau, le brouillon (chantier SUP) ──

export type SupportTab = 'new' | 'list';

export interface SupportDraft {
  kind: SupportKind;
  body: string;
  shots: DraftShot[];
  /** R4 : joindre la copie du projet a un probleme (coche par defaut) */
  attachCopy: boolean;
}

export const EMPTY_DRAFT: SupportDraft = { kind: 'BUG', body: '', shots: [], attachCopy: true };

/** Ce que le reste de l'application peut faire du support */
export interface SupportApi {
  /** Ouvre le panneau sur un onglet, ou sur le fil d'une demande */
  open: (tab?: SupportTab, thread?: number) => void;
  close: () => void;
  isOpen: boolean;
}

interface SupportContextValue extends SupportApi {
  /** Le support est offert sur cette page (R1) */
  visible: boolean;
  tab: SupportTab;
  draft: SupportDraft;
  setDraft: React.Dispatch<React.SetStateAction<SupportDraft>>;
  email: string;
  /** Le fil ouvert dans « Mes demandes », ou null pour la liste */
  thread: number | null;
  setTab: (tab: SupportTab) => void;
  setThread: (thread: number | null) => void;
  /** Mes reponses du support non lues : la pastille (R9) */
  unread: number;
  refreshUnread: () => void;
  /** La derniere reponse arrivee en direct (dans l'editeur) : le fil ouvert sur cette demande se relit */
  lastLive: { ticketNumber: number; at: number } | null;
  /** Une capture est en cours : le panneau se masque pour ne pas figurer sur l'image */
  capturing: boolean;
  setCapturing: (capturing: boolean) => void;
  /** Une reponse du support arrivee par le direct de l'editeur */
  onLive: (event: SupportEvent) => void;
  /** L'editeur monte : le bouton passe au-dessus de sa barre du bas, et se reduit pendant le dessin */
  setEditor: (editor: EditorPresence | null) => void;
}

interface EditorPresence {
  compact: boolean;
}

const SupportContext = createContext<SupportContextValue | null>(null);

/**
 * R1 : pas de support sur les pages publiques (centre d'aide, pages legales, connexion), ni sur l'acceptation des CGU,
 * dans l'administration, ni dans la page de capture du studio video.
 */
const HIDDEN_PREFIXES = ['/admin', '/video-capture', '/aide', ...LEGAL_PATHS, '/conditions', '/login', '/register', '/forgot-password', '/reset-password', '/finalize-registration'];
export const supportVisibleOn = (pathname: string) =>
  !HIDDEN_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`));

export function SupportProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const pathname = usePathname() ?? '/';
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<SupportTab>('new');
  const [draft, setDraft] = useState<SupportDraft>(EMPTY_DRAFT);
  const [editor, setEditor] = useState<EditorPresence | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [thread, setThread] = useState<number | null>(null);
  const [unread, setUnread] = useState(0);
  const [lastLive, setLastLive] = useState<{ ticketNumber: number; at: number } | null>(null);
  const router = useRouter();

  // Le collecteur ecoute la page des le chargement : les erreurs d'avant la demande comptent (R6)
  useEffect(() => { collector.install(window); }, []);

  const open = useCallback((next: SupportTab = 'new', ticket?: number) => {
    setTab(next);
    setThread(next === 'list' ? ticket ?? null : null);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  // L'utilisateur est connu tant que la session dure, meme pendant qu'elle se rafraichit
  const userId = session?.user?.id;
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => {
    if (status === 'authenticated' && userId) setSignedIn(true);
    if (status === 'unauthenticated') setSignedIn(false);
  }, [status, userId]);
  const visible = signedIn && supportVisibleOn(pathname);
  // Changer de page vers une page sans support ferme le panneau ; le brouillon reste
  useEffect(() => { if (!visible) setIsOpen(false); }, [visible]);

  // La pastille : lue au chargement et a chaque ouverture du panneau ; dans l'editeur, le direct la tient a jour
  const refreshUnread = useCallback(() => {
    fetch('/api/support/unread')
      .then(res => (res.ok ? res.json() as Promise<{ unread: number }> : null))
      .then(data => { if (data) setUnread(data.unread); })
      .catch(() => {});
  }, []);
  useEffect(() => { if (signedIn) refreshUnread(); }, [signedIn, refreshUnread]);
  useEffect(() => { if (isOpen) refreshUnread(); }, [isOpen, refreshUnread]);

  // Le lien du mail de reponse (?support=<n>) ouvre le fil de la demande, puis s'efface de l'adresse
  useEffect(() => {
    if (!visible) return;
    const params = new URLSearchParams(window.location.search);
    const number = parseTicketNumber(params.get('support') ?? '');
    if (number === null) return;
    open('list', number);
    params.delete('support');
    const rest = params.toString();
    router.replace(`${pathname}${rest ? `?${rest}` : ''}${window.location.hash}`, { scroll: false });
  }, [visible, pathname, open, router]);

  const setLiveEvent = useCallback((event: SupportEvent) => {
    setUnread(event.unread);
    setLastLive({ ticketNumber: event.ticketNumber, at: Date.now() });
  }, []);

  const value = useMemo<SupportContextValue | null>(() => (signedIn ? {
    open, close, isOpen: isOpen && visible, visible, tab, draft, setDraft, email: session?.user?.email ?? '',
    thread, setTab, setThread, unread, refreshUnread, lastLive, onLive: setLiveEvent,
    capturing, setCapturing, setEditor,
  } : null), [signedIn, visible, open, close, isOpen, tab, draft, session?.user?.email, thread, unread, refreshUnread, lastLive, capturing]);

  return (
    <SupportContext.Provider value={value}>
      {children}
      {/* Le panneau reste monte pendant toute la session, meme sur une page sans support : un envoi en cours aboutit */}
      {value && <SupportPanel />}
      {/* Une reponse non lue : le clic mene a « Mes demandes » */}
      {visible && !isOpen && (
        <SupportButton onClick={() => open(unread > 0 ? 'list' : 'new')} inEditor={!!editor} compact={!!editor?.compact} unread={unread} />
      )}
    </SupportContext.Provider>
  );
}

/** Le support, ou null s'il n'est pas offert ici (non connecte, page publique, administration) */
export function useSupport(): SupportApi | null {
  const ctx = useContext(SupportContext);
  return ctx?.visible ? ctx : null;
}

/** Pour le panneau : l'etat complet */
export function useSupportPanel(): SupportContextValue {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error('useSupportPanel hors de SupportProvider');
  return ctx;
}

/** L'editeur se signale au support tant qu'il est monte ; compact pendant qu'un outil de dessin est actif */
export function useSupportEditor(compact: boolean) {
  const setEditor = useContext(SupportContext)?.setEditor;
  useEffect(() => {
    if (!setEditor) return;
    setEditor({ compact });
  }, [setEditor, compact]);
  useEffect(() => {
    if (!setEditor) return;
    return () => setEditor(null);
  }, [setEditor]);
}

/** Dans l'editeur : les reponses du support arrivent par son flux en direct (la pastille, le fil ouvert) */
export function useSupportLive(live: LiveEventsHandle) {
  const onLive = useContext(SupportContext)?.onLive;
  useEffect(() => (onLive ? live.subscribe('support', onLive) : undefined), [live, onLive]);
}
